<template>
  <div class="admin-chapter-roasts-page">
    <div class="page-container">
      <header class="page-header">
        <h1 class="page-title">Admin Panel</h1>
        <nav class="admin-nav">
          <router-link to="/admin/users" class="nav-tab">Users</router-link>
          <router-link to="/admin/feedback" class="nav-tab">Feedback</router-link>
          <router-link to="/admin/chapter-roasts" class="nav-tab active">Chapter Roasts</router-link>
          <router-link to="/admin/consults" class="nav-tab">Consults</router-link>
        </nav>
      </header>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
      </div>

      <template v-else>
        <h2 class="section-heading">Roasts</h2>
        <div class="stats-row">
          <div class="stat-card">
            <div class="stat-value">{{ stats.total.toLocaleString() }}</div>
            <div class="stat-label">Roasts</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ (funnel?.uniqueLeads ?? 0).toLocaleString() }}</div>
            <div class="stat-label">Leads</div>
          </div>
          <div class="stat-card">
            <div class="stat-value error">{{ stats.error.toLocaleString() }}</div>
            <div class="stat-label">Errored</div>
          </div>
          <div class="stat-card">
            <div class="stat-value error">{{ fmtMoney(funnel?.totalCostUsd ?? 0) }}</div>
            <div class="stat-label">Roast cost</div>
          </div>
        </div>

        <h2 class="section-heading">Funnel stats</h2>
        <div class="stats-row">
          <div class="stat-card">
            <div class="stat-value complete">{{ (funnel?.converted ?? 0).toLocaleString() }}</div>
            <div class="stat-label">Conversions</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ funnel?.conversionRate ?? 0 }}%</div>
            <div class="stat-label">Conversion rate</div>
          </div>
          <div class="stat-card">
            <div class="stat-value complete">{{ fmtMoney((funnel?.revenueCents ?? 0) / 100) }}</div>
            <div class="stat-label">Revenue</div>
          </div>
          <div class="stat-card" title="Conversion revenue − roast cost">
            <div class="stat-value" :class="(funnel?.netUsd ?? 0) >= 0 ? 'complete' : 'error'">
              {{ fmtMoney(funnel?.netUsd ?? 0) }}
            </div>
            <div class="stat-label">Net</div>
          </div>
        </div>

        <div v-if="items.length === 0" class="empty-state">No chapter roasts yet</div>

        <div v-else class="edits-list">
          <div v-for="edit in items" :key="edit.id" class="edit-card" :class="{ 'is-deleted': edit.deletedAt }">
            <a :href="`/edit/${edit.id}`" target="_blank" class="edit-info">
              <div class="edit-title">{{ edit.title || 'Untitled' }}</div>
              <div class="edit-meta">
                {{ edit.email }} · {{ edit.wordCount.toLocaleString() }} words · {{ fmtCost(edit.costUsd) }}
              </div>
            </a>
            <div class="edit-right">
              <span v-if="edit.converted" class="edit-status status-converted">
                Converted · {{ fmtMoney(edit.revenueCents / 100) }}
              </span>
              <span v-if="edit.deletedAt" class="edit-status status-deleted">Deleted</span>
              <span v-else class="edit-status" :class="`status-${edit.status}`">{{ edit.status }}</span>
              <span class="edit-date">{{ formatDate(edit.createdAt) }}</span>
            </div>
            <div class="edit-admin-actions">
              <button v-if="edit.deletedAt" class="action-btn restore" @click="handleRestore(edit)">Restore</button>
              <button v-else class="action-btn delete" @click="handleDelete(edit)">Delete</button>
            </div>
          </div>
        </div>

        <div v-if="total > items.length" class="load-more">
          <button @click="loadMore" :disabled="loadingMore" class="load-more-btn">
            {{ loadingMore ? 'Loading…' : `Load more (${total - items.length} remaining)` }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { deleteProject, restoreProject } from '../services/api';
import { useAuthStore } from '../stores/auth';

interface FrameworkProject {
  id: string;
  title: string | null;
  email: string;
  wordCount: number;
  status: string;
  createdAt: string;
  costUsd: number | null;
  converted: boolean;
  revenueCents: number;
  deletedAt: string | null;
}

interface FrameworkStats {
  total: number;
  complete: number;
  processing: number;
  error: number;
}

interface FunnelSummary {
  totalRoasts: number;
  uniqueLeads: number;
  converted: number;
  conversionRate: number;
  revenueCents: number;
  totalCostUsd: number;
  avgCostPerRoastUsd: number;
  netUsd: number;
}

const FRAMEWORK = 'chapter-roast';
const PAGE_SIZE = 20;

const authStore = useAuthStore();
const loading = ref(true);
const loadingMore = ref(false);
const items = ref<FrameworkProject[]>([]);
const total = ref(0);
const stats = ref<FrameworkStats>({ total: 0, complete: 0, processing: 0, error: 0 });
const funnel = ref<FunnelSummary | null>(null);

function fmtMoney(usd: number): string {
  return `$${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtCost(costUsd: number | null): string {
  if (costUsd == null) return '—';
  return `$${costUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: costUsd < 1 ? 4 : 2 })}`;
}

async function fetchProjects(offset = 0) {
  const token = await authStore.getIdToken();
  const response = await fetch(`/api/admin/frameworks/${FRAMEWORK}/projects?limit=${PAGE_SIZE}&offset=${offset}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch projects');
  return response.json() as Promise<{ items: FrameworkProject[]; total: number }>;
}

async function fetchStats() {
  const token = await authStore.getIdToken();
  const response = await fetch(`/api/admin/frameworks/${FRAMEWORK}/stats`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch stats');
  return response.json() as Promise<FrameworkStats>;
}

async function fetchFunnel() {
  const token = await authStore.getIdToken();
  const response = await fetch(`/api/admin/frameworks/${FRAMEWORK}/funnel`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch funnel');
  return response.json() as Promise<FunnelSummary>;
}

async function loadInitial() {
  try {
    const [statsData, funnelData, projectsData] = await Promise.all([fetchStats(), fetchFunnel(), fetchProjects(0)]);
    stats.value = statsData;
    funnel.value = funnelData;
    items.value = projectsData.items;
    total.value = projectsData.total;
  } catch (error) {
    console.error('Failed to load chapter roasts:', error);
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  loadingMore.value = true;
  try {
    const data = await fetchProjects(items.value.length);
    items.value.push(...data.items);
    total.value = data.total;
  } catch (error) {
    console.error('Failed to load more chapter roasts:', error);
  } finally {
    loadingMore.value = false;
  }
}

async function handleDelete(edit: FrameworkProject) {
  if (!window.confirm('Delete this chapter roast? It will be permanently purged after 30 days.')) return;
  try {
    await deleteProject(edit.id);
    edit.deletedAt = new Date().toISOString();
  } catch (error) {
    console.error('Failed to delete project:', error);
    window.alert('Failed to delete. Try again.');
  }
}

async function handleRestore(edit: FrameworkProject) {
  try {
    await restoreProject(edit.id);
    edit.deletedAt = null;
  } catch (error) {
    console.error('Failed to restore project:', error);
    window.alert('Failed to restore. Try again.');
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

onMounted(loadInitial);
</script>

<style scoped>
.admin-chapter-roasts-page {
  flex: 1;
  background: #f7fafc;
}

.page-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2.25rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
}

.admin-nav {
  display: flex;
  gap: 0.25rem;
  border-bottom: 1px solid #e2e8f0;
}

.nav-tab {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #718096;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.15s;
}

.nav-tab:hover {
  color: #1a202c;
}

.nav-tab.active,
.nav-tab.router-link-active {
  color: var(--color-accent);
  border-bottom-color: var(--color-accent);
}

.loading-state {
  text-align: center;
  padding: 4rem 2rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.section-heading {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #718096;
  margin-bottom: 0.75rem;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.75rem;
}

.stat-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem;
  text-align: center;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
}

.stat-value.complete {
  color: #22543d;
}

.stat-value.processing {
  color: #2a4365;
}

.stat-value.error {
  color: #742a2a;
}

.stat-label {
  font-size: 0.75rem;
  color: #718096;
  margin-top: 0.25rem;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #718096;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.edits-list {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.edit-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  transition: background 0.15s;
}

.edit-card:last-child {
  border-bottom: none;
}

.edit-card:hover {
  background: #f7fafc;
}

.edit-card.is-deleted {
  opacity: 0.6;
}

.edit-info {
  flex: 1;
  min-width: 0;
  text-decoration: none;
}

.edit-admin-actions {
  flex-shrink: 0;
}

.action-btn {
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}

.action-btn.delete {
  color: #e53e3e;
  background: #fff5f5;
  border-color: #fed7d7;
}

.action-btn.delete:hover {
  background: #fed7d7;
}

.action-btn.restore {
  color: #2a4365;
  background: #ebf8ff;
  border-color: #bee3f8;
}

.action-btn.restore:hover {
  background: #bee3f8;
}

.edit-status.status-deleted {
  background: #fed7d7;
  color: #742a2a;
}

.edit-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a202c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.edit-meta {
  font-size: 0.75rem;
  color: #718096;
  margin-top: 0.125rem;
}

.edit-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  flex-shrink: 0;
}

.edit-status {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  background: #edf2f7;
  color: #4a5568;
}

.edit-status.status-complete {
  background: #c6f6d5;
  color: #22543d;
}

.edit-status.status-processing {
  background: #bee3f8;
  color: #2a4365;
}

.edit-status.status-error {
  background: #fed7d7;
  color: #742a2a;
}

.edit-status.status-converted {
  background: #22543d;
  color: #f0fff4;
}

.edit-date {
  font-size: 0.75rem;
  color: #a0aec0;
}

.load-more {
  text-align: center;
  margin-top: 1.5rem;
}

.load-more-btn {
  padding: 0.625rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}

.load-more-btn:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.load-more-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
